import { useState, type ComponentType, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { HistoryModal } from './HistoryModal';
import { HistoryTable } from './HistoryTable';
import { HistoryTabs } from './HistoryTabs';
import type { HistoryTab } from './types';

/** Rows shown in the card; the rest are in View All. */
export const HISTORY_PREVIEW_ROWS = 15;

interface HistoryCardProps {
  tabs: HistoryTab[];
  /** Modal title when there are several tabs (one tab uses its own label). */
  title: string;
  /** Line under the modal title, e.g. "#27 - Arun Prakash · P2701". */
  subtitle?: ReactNode;
  /** Icon before the label when there is a single tab. */
  icon?: ComponentType<{ className?: string }>;
  /** Extra controls left of View All, e.g. a filter. */
  actions?: ReactNode;
  /** Shown right after the title when there is a single tab, e.g. a policy badge. */
  titleExtra?: ReactNode;
  defaultTabId?: string;
}

/**
 * Every history in the app looks the same: tabs (or a title) on the left, View All on the
 * right, the latest 15 rows below. View All opens the full list with Export.
 */
export function HistoryCard({ tabs, title, subtitle, icon: Icon, actions, titleExtra, defaultTabId }: HistoryCardProps) {
  const [activeId, setActiveId] = useState(defaultTabId ?? tabs[0].id);
  const [showAll, setShowAll] = useState(false);
  const tab = tabs.find((item) => item.id === activeId) ?? tabs[0];
  const hiddenRows = tab.rows.length - HISTORY_PREVIEW_ROWS;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
      <div className="flex min-h-[50px] flex-wrap items-end justify-between gap-3 border-b border-gray-200 px-4">
        {tabs.length > 1 ? (
          <HistoryTabs tabs={tabs} activeId={tab.id} onChange={setActiveId} />
        ) : (
          <h3 className="flex items-center gap-2 self-center text-sm font-semibold text-gray-900">
            {Icon && <Icon className="h-4 w-4 text-[#4369a0]" />}
            {tab.label}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{tab.rows.length}</span>
            {titleExtra}
          </h3>
        )}
        <div className="flex items-center gap-3 self-center py-2">
          {actions}
          <Button type="button" variant="link" className="h-auto p-0 text-[#4369a0]" onClick={() => setShowAll(true)}>
            View All
          </Button>
        </div>
      </div>

      <div className="space-y-3 p-4">
        <HistoryTable columns={tab.columns} rows={tab.rows.slice(0, HISTORY_PREVIEW_ROWS)} empty={tab.empty} />
        {hiddenRows > 0 && (
          <p className="text-[13px] text-gray-500">
            Showing latest {HISTORY_PREVIEW_ROWS} of {tab.rows.length}
          </p>
        )}
      </div>

      <HistoryModal
        open={showAll}
        onOpenChange={setShowAll}
        title={tabs.length > 1 ? title : tab.label}
        subtitle={subtitle}
        tabs={tabs}
        activeId={tab.id}
        onTabChange={setActiveId}
      />
    </section>
  );
}
